/**
 * The built brain, kept on the phone (job #194) so a repeat open draws at once.
 *
 * Building the graph needs the whole course bundle out of IndexedDB — 10 MB
 * and more on a late course, deserialised on the main thread — and then a walk
 * over every phrase. The finished graph is a few hundred kB. So the graph
 * itself is kept, one record per course, and the next open draws from it
 * before anything else is read; the bundle is only read again when the
 * learner's diary or the course's content version has moved on.
 *
 * Stored as ONE JSON string, not as the object graph: a French graph is
 * ~20,000 small objects, and structured-cloning those cost ~750 ms to write
 * and ~400 ms to read on a throttled phone; a single string is copied whole
 * and JSON.parse is the fastest way back to objects.
 *
 * Bounded by construction: one record per course the learner has a brain on.
 * Learner-scoped: a record answers only the identity that built it, and the
 * whole store goes on sign-out with the course bundles.
 */
import type { BrainGraph } from './brainGraph'

const DB_NAME = 'ssi-brain'
const DB_VERSION = 1
const STORE = 'graphs'
/** Bump when buildBrainGraph's output changes shape: every kept graph is then rebuilt once. */
export const GRAPH_SHAPE = 3

export interface KeptBrain {
  courseCode: string
  /** the learner id that built it */
  owner: string | null
  /** the bundle's content and script-shape versions it was built from */
  contentVersion: string
  scriptShapeVersion: number
  /** hash of the diary it was built from */
  activityHash: string
  /** the ETag that diary came with (job #195): a 304 for it confirms this graph as it stands */
  etag?: string
  shape: number
  savedAt: number
  graph: BrainGraph
}

type StoredBrain = Omit<KeptBrain, 'graph'> & { json: string }

/** cyrb53: a 53-bit string hash, plenty to tell one diary from the next. */
export function hashText(s: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    h1 = Math.imul(h1 ^ c, 2654435761)
    h2 = Math.imul(h2 ^ c, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36)
}

/**
 * Bumped by every sign-out clear. A keep or an activity write that began before
 * the clear (idle-deferred, or a request still in flight) carries the epoch it
 * began under and is dropped if it no longer matches, so a late write cannot
 * put the signed-out learner's brain back on the phone.
 */
let epoch = 0
export const brainEpoch = (): number => epoch

let dbPromise: Promise<IDBDatabase | null> | null = null
function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null)
    let req: IDBOpenDBRequest
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      return resolve(null)
    }
    // A blocked upgrade must never hold the picture: give up and draw the slow way.
    const t = setTimeout(() => resolve(null), 1500)
    req.onupgradeneeded = () => {
      const db = req.result
      if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE)
      db.createObjectStore(STORE, { keyPath: 'courseCode' })
    }
    req.onsuccess = () => {
      clearTimeout(t)
      const db = req.result
      db.onversionchange = () => { db.close(); dbPromise = null }
      resolve(db)
    }
    req.onerror = () => { clearTimeout(t); resolve(null) }
  })
  void dbPromise.then((db) => { if (!db) dbPromise = null })
  return dbPromise
}

/** The kept graph for this course, when it is this learner's and the current shape. */
export async function readKeptBrain(courseCode: string, owner: string | null): Promise<KeptBrain | null> {
  const db = await openDb()
  if (!db) return null
  const rec = await new Promise<StoredBrain | null>((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(courseCode)
      req.onsuccess = () => resolve((req.result as StoredBrain) ?? null)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
  if (!rec?.json || rec.shape !== GRAPH_SHAPE || rec.owner !== owner) return null
  try {
    const { json, ...meta } = rec
    return { ...meta, graph: JSON.parse(json) as BrainGraph }
  } catch {
    return null
  }
}

export async function keepBrain(rec: Omit<KeptBrain, 'shape' | 'savedAt'>, startedEpoch?: number): Promise<void> {
  if (startedEpoch !== undefined && startedEpoch !== epoch) return
  const db = await openDb()
  if (!db) return
  if (startedEpoch !== undefined && startedEpoch !== epoch) return
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      const { graph, ...meta } = rec
      const stored: StoredBrain = { ...meta, json: JSON.stringify(graph), shape: GRAPH_SHAPE, savedAt: Date.now() }
      tx.objectStore(STORE).put(stored)
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
      tx.onabort = () => resolve()
    } catch {
      resolve()
    }
  })
}

/** Sign-out: nobody's brain stays behind for the next person on this phone. */
export async function clearKeptBrains(): Promise<void> {
  epoch++
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k && (k.startsWith('ssi-brain-') || k.startsWith('ssi-phrase-plays-'))) localStorage.removeItem(k)
    }
  } catch { /* no storage: nothing kept */ }
  const db = await openDb()
  if (!db) return
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).clear()
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
    } catch {
      resolve()
    }
  })
}
